import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, BookOpen } from "lucide-react";
import { SEO } from "../components/shared/SEO";
import { Reveal, ImageReveal } from "../components/shared/Reveal";
import { PreviewForm } from "../components/book/PreviewForm";
import { OrderBox } from "../components/book/OrderBox";
import { BOOK_ABOUT, BOOK_THEMES, BOOK_METHOD, AUTHOR } from "../content/book";
import { api, imgUrl } from "../lib/api";

const EASE = [0.16, 1, 0.3, 1];

const RouteLines = ({ className = "" }) => (
  <svg aria-hidden="true" className={`pointer-events-none absolute inset-0 w-full h-full ${className}`} viewBox="0 0 1200 600" preserveAspectRatio="none">
    <path d="M-20 480 C 200 420, 380 520, 600 400 S 1000 260, 1240 300" fill="none" stroke="#CBB279" strokeWidth="1" strokeDasharray="6 10" opacity="0.45" />
    <path d="M-20 120 C 300 160, 500 60, 800 140 S 1100 220, 1240 160" fill="none" stroke="#ffffff" strokeWidth="0.6" strokeDasharray="2 8" opacity="0.25" />
    {[[600, 400], [800, 140], [200, 440]].map(([x, y]) => <circle key={x} cx={x} cy={y} r="3" fill="#CBB279" opacity="0.7" />)}
  </svg>
);

const Cover = ({ src, title }) => {
  const [url, setUrl] = useState(imgUrl(src));
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!src || loaded) return;
    const t = setTimeout(() => setUrl("/book-cover.jpg"), 3500);
    return () => clearTimeout(t);
  }, [src, loaded]);
  return (
  <div className="relative w-[190px] sm:w-[240px] md:w-[300px] mx-auto book-cover-enter" data-testid="book-cover" style={{ perspective: 1200 }}>
    <div className="relative shadow-[30px_40px_80px_-20px_rgba(0,0,0,0.7)] md:[transform:rotateY(-14deg)]">
      {src ? <img src={url} alt={`Copertina del libro ${title} di Giovanni Luca Costantino`} width={1200} height={1800} className="block w-full h-auto aspect-[2/3] object-cover bg-navy-soft" fetchPriority="high" onLoad={() => setLoaded(true)} onError={() => setUrl("/book-cover.jpg")} /> : (
        <div className="w-full aspect-[2/3] bg-navy-soft border border-cream/20 flex flex-col justify-between p-7">
          <div className="text-[9px] uppercase tracking-[0.3em] text-gold">Giovanni Luca Costantino</div>
          <div><div className="font-serif text-4xl font-light text-cream leading-[1.05]">La Rotta<br />del Valore</div><div className="w-10 h-px bg-gold mt-5" /></div>
          <div className="text-[9px] uppercase tracking-[0.25em] text-cream/50">Copertina in arrivo</div>
        </div>
      )}
    </div>
  </div>
  );
};

const Quotes = ({ quotes }) => {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % quotes.length), 6000); return () => clearInterval(t); }, [quotes.length]);
  if (!quotes.length) return null;
  return (
    <div data-testid="book-quotes-slider" className="relative text-center max-w-4xl mx-auto min-h-[180px] flex flex-col justify-center">
      <motion.p key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="font-serif italic font-light text-3xl md:text-4xl lg:text-5xl tracking-tight text-cream">“{quotes[i]}”</motion.p>
      <div className="mt-8 text-[10px] uppercase tracking-[0.3em] text-gold">La Rotta del Valore</div>
      <div className="flex items-center justify-center gap-6 mt-8">
        <button aria-label="Citazione precedente" data-testid="quote-prev" onClick={() => setI((i - 1 + quotes.length) % quotes.length)} className="text-cream/50 hover:text-gold"><ChevronLeft size={20} strokeWidth={1} /></button>
        <div className="flex gap-2">{quotes.map((_, j) => <span key={j} className={`h-px transition-all duration-500 ${j === i ? "w-8 bg-gold" : "w-3 bg-cream/30"}`} />)}</div>
        <button aria-label="Citazione successiva" data-testid="quote-next" onClick={() => setI((i + 1) % quotes.length)} className="text-cream/50 hover:text-gold"><ChevronRight size={20} strokeWidth={1} /></button>
      </div>
    </div>
  );
};

export default function RottaDelValore() {
  const { data: book } = useQuery({ queryKey: ["book"], queryFn: () => api.get("/book").then((r) => r.data) });
  const [allChapters, setAllChapters] = useState(false);

  useEffect(() => {
    if (!book) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "ld-book";
    el.text = JSON.stringify({ "@context": "https://schema.org", "@type": "Book", name: book.title, alternativeName: "Leadership, metodo e risultati: il viaggio di un investitore tra mare, impresa e sviluppo immobiliare", isbn: "979-12-24049-44-9", inLanguage: "it", numberOfPages: 166, bookFormat: "https://schema.org/Paperback", image: book.cover_image ? imgUrl(book.cover_image) : undefined, author: { "@type": "Person", name: AUTHOR.name, jobTitle: AUTHOR.role, worksFor: { "@type": "Organization", name: "Home Haven Srl" } }, publisher: { "@type": "Organization", name: "Home Haven Srl" }, offers: book.sale_status !== "preparazione" && book.price ? { "@type": "Offer", price: book.price.replace(/[^\d,.]/g, "").replace(",", "."), priceCurrency: "EUR", availability: "https://schema.org/InStock" } : undefined });
    document.getElementById("ld-book")?.remove();
    document.head.appendChild(el);
    return () => el.remove();
  }, [book]);

  if (!book) return <div className="min-h-screen bg-navy" />;

  return (
    <>
      <SEO title="La Rotta del Valore | Libro di Giovanni Luca Costantino" description="Scopri La Rotta del Valore, il libro di Giovanni Luca Costantino dedicato a visione, metodo e creazione di valore personale e imprenditoriale." image={book.cover_image ? imgUrl(book.cover_image) : undefined} />

      <section data-testid="book-hero" className="relative min-h-[100svh] flex items-center bg-navy text-cream overflow-hidden">
        <img src={imgUrl(book.hero_bg_image)} alt="" className="absolute inset-0 img-cover opacity-35" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/40" />
        <RouteLines />
        <div className="container-hh relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center pt-28 pb-24 lg:py-40">
          <div className="lg:col-span-5 order-first lg:order-last"><Cover src={book.cover_image} title={book.title} /></div>
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="eyebrow mb-6">Il libro di Giovanni Luca Costantino</motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.35, ease: EASE }} className="h-display" data-testid="book-title">{book.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.55 }} className="font-serif italic text-2xl md:text-3xl font-light text-beige mt-8 max-w-2xl">{book.subtitle}</motion.p>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7 }} className="lead !text-cream/75 mt-6 max-w-xl">{book.hero_text}</motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85 }} className="flex flex-wrap gap-4 mt-12">
              <a href="#anteprima" data-testid="book-hero-cta-anteprima" className="btn-light">Richiedi l'anteprima</a>
              <a href="#storia" data-testid="book-hero-cta-storia" className="btn-outline-light">Scopri la storia</a>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="storia" className="section bg-cream scroll-mt-20">
        <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5"><div className="eyebrow mb-6">Il libro</div><h2 className="h-section">{BOOK_ABOUT.title}</h2><div className="mt-8 flex items-center gap-4 text-navy"><BookOpen strokeWidth={1} size={28} /><span className="text-xs uppercase tracking-[0.25em]">18 capitoli</span></div></Reveal>
          <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">{BOOK_ABOUT.paragraphs.map((p, i) => <p key={i} className="lead mb-5">{p}</p>)}</Reveal>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-hh">
          <Reveal className="mb-16"><div className="eyebrow mb-6">I temi del viaggio</div><h2 className="h-section max-w-3xl">Sei coordinate per orientarsi.</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10">
            {BOOK_THEMES.map((t, i) => (
              <Reveal key={t.title} delay={(i % 3) * 0.1} className="bg-white" data-testid={`book-theme-${i}`}>
                <div className="group h-full p-8 md:p-10 bg-white hover:bg-cream transition-colors duration-500 min-h-[260px] flex flex-col">
                  <div className="flex items-start justify-between"><t.icon size={28} strokeWidth={1} className="text-navy group-hover:text-gold transition-colors duration-500" /><span className="font-serif text-2xl text-navy/20">0{i + 1}</span></div>
                  <h3 className="mt-auto pt-12 font-sans text-xs uppercase tracking-[0.25em] font-medium text-ink">{t.title}</h3>
                  <div className="w-8 h-px bg-gold my-4 group-hover:w-16 transition-all duration-500" />
                  <p className="text-sm md:text-base font-light leading-relaxed text-ink/70">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section relative bg-navy text-cream overflow-hidden">
        <RouteLines className="opacity-60" />
        <div className="container-hh relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
            <Reveal className="lg:col-span-5"><div className="eyebrow mb-6">Dalla rotta al progetto</div><h2 className="h-section">{BOOK_METHOD.title}</h2></Reveal>
            <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">{BOOK_METHOD.paragraphs.map((p, i) => <p key={i} className="lead !text-cream/75 mb-5">{p}</p>)}</Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6 border-t border-cream/10 pt-14">
            {BOOK_METHOD.phases.map((ph, i) => (
              <Reveal key={ph.title} delay={i * 0.12} data-testid={`book-phase-${i}`}>
                <div className="font-serif text-5xl font-light text-gold leading-none">0{i + 1}</div>
                <h3 className="mt-5 font-sans text-xs uppercase tracking-[0.25em] font-medium">{ph.title}</h3>
                <p className="mt-3 text-sm font-light text-cream/70 leading-relaxed">{ph.text}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16"><Link to="/progetti" data-testid="book-cta-progetti" className="btn-light">Scopri i progetti Home Haven <ArrowRight size={14} /></Link></Reveal>
        </div>
      </section>

      <section className="section bg-cream">
        <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            {book.author_photo ? <ImageReveal src={imgUrl(book.author_photo)} alt={`${AUTHOR.name}, autore de La Rotta del Valore`} className="aspect-[4/5]" /> : (
              <div data-testid="author-photo-placeholder" className="aspect-[4/5] bg-navy flex items-end p-8"><div><div className="font-serif text-6xl font-light text-gold">GC</div><div className="text-[10px] uppercase tracking-[0.25em] text-cream/50 mt-3">Fotografia in arrivo</div></div></div>
            )}
          </div>
          <Reveal className="lg:col-span-6 lg:col-start-7 lg:pt-10">
            <div className="eyebrow mb-6">L'autore</div>
            <h2 className="h-section">{AUTHOR.name}</h2>
            <p className="font-serif italic text-xl md:text-2xl text-navy mt-3">{AUTHOR.role}</p>
            <div className="mt-8">{AUTHOR.bio.map((p, i) => <p key={i} className="lead mb-4">{p}</p>)}</div>
            {book.author_story_url ? <a href={book.author_story_url} data-testid="author-story-link" className="btn-link mt-8">Conosci la storia di Giovanni Luca</a> : <a href="#storia" data-testid="author-story-link" className="btn-link mt-8">Conosci la storia di Giovanni Luca</a>}
          </Reveal>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-hh">
          <Reveal className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6"><div><div className="eyebrow mb-6">Anteprima dei capitoli</div><h2 className="h-section">Le tappe del viaggio.</h2></div><span className="text-xs uppercase tracking-[0.2em] text-stone">{book.chapters.length} capitoli</span></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(allChapters ? book.chapters : book.chapters.slice(0, 6)).map((c, i) => (
              <Reveal key={i} delay={(i % 3) * 0.1} data-testid={`book-chapter-${c.n}`} className="group border border-navy/10 hover:border-gold/60 transition-colors duration-500 flex flex-col">
                {c.image && <ImageReveal src={imgUrl(c.image)} alt={`Capitolo ${c.n}: ${c.title}`} className="aspect-[16/10]" />}
                <div className="p-8 flex flex-col flex-1">
                  <div className="font-serif text-4xl font-light text-gold">{String(c.n).padStart(2, "0")}</div>
                  <h3 className="h-card !text-2xl mt-3">{c.title}</h3>
                  <p className="text-sm font-light text-ink/70 leading-relaxed mt-3">{c.description}</p>
                  {c.quote && <p className="font-serif italic text-lg text-navy mt-5">“{c.quote}”</p>}
                  <a href="#anteprima" data-testid={`chapter-more-${c.n}`} className="btn-link mt-auto pt-6 self-start">Approfondisci</a>
                </div>
              </Reveal>
            ))}
          </div>
          {book.chapters.length > 6 && (
            <div className="mt-12 text-center"><button type="button" data-testid="chapters-toggle" onClick={() => setAllChapters(!allChapters)} className="btn-outline">{allChapters ? "Mostra meno" : `Vedi tutti i ${book.chapters.length} capitoli`}</button></div>
          )}
        </div>
      </section>

      <section className="section bg-navy text-cream relative overflow-hidden">
        <RouteLines className="opacity-40" />
        <div className="container-hh relative"><Quotes quotes={book.quotes} /></div>
      </section>

      <section id="anteprima" className="section bg-cream scroll-mt-20">
        <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4"><div className="eyebrow mb-6">Anteprima gratuita</div><h2 className="h-section">Ricevi gratuitamente l'anteprima</h2><p className="lead mt-6">Lascia i tuoi dati per ricevere l'introduzione e il primo capitolo de La Rotta del Valore.</p></Reveal>
          <div className="lg:col-span-7 lg:col-start-6"><PreviewForm previewUrl={book.preview_file ? imgUrl(book.preview_file) : ""} /></div>
        </div>
      </section>

      <section id="acquista" className="section bg-white">
        <div className="container-hh">
          <Reveal className="mb-12"><div className="eyebrow mb-6">Il volume</div><h2 className="h-section">Porta la rotta con te.</h2></Reveal>
          <Reveal><OrderBox book={book} /></Reveal>
        </div>
      </section>

      <section className="section relative bg-navy text-cream overflow-hidden">
        <img src={imgUrl(book.hero_bg_image)} alt="" loading="lazy" className="absolute inset-0 img-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/70 to-navy" />
        <div className="container-hh relative text-center max-w-4xl mx-auto">
          <Reveal>
            <div className="eyebrow mb-6">La tua rotta</div>
            <h2 className="h-display">Qual è la tua prossima rotta?</h2>
            <p className="lead !text-cream/75 mt-8 max-w-2xl mx-auto">Ogni trasformazione comincia da una scelta. Scopri La Rotta del Valore e il metodo con cui Home Haven trasforma immobili, idee e opportunità in nuovi progetti.</p>
            <div className="flex flex-wrap justify-center gap-4 mt-12">
              <a href="#anteprima" data-testid="book-final-cta-anteprima" className="btn-light">Richiedi l'anteprima</a>
              <Link to="/contatti" data-testid="book-final-cta-contatti" className="btn-outline-light">Parla con Home Haven</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

